// See docs for order of panes: https://developers.google.com/maps/documentation/javascript/customoverlays#intitialize
const getPaneType = level => {
  // The panes order matches the docs
  const panes = [
    'mapPane',
    'overlayLayer',
    'markerLayer',
    'overlayMouseTarget',
    'floatPane',
  ];

  if (level >= 0 && level <= 4) {
    return panes[level];
  }
  // return floatPane by default
  return panes[4];
};

/**
 * Extend the Google Maps OverlayView class to handle dynamic content updates
 *  within a React Portal component that would otherwise be difficult or
 * not be possible with an infowindow. The component needs to be wrapped in a
 * <Provider> if it needs to access Redux.
 * Usage of this class:
 * // Component.js
 * const createCustomOverlay = () => {
 * (async () => {
 *   const { default: CustomOverlay } = await import('PATH/TO/CustomOverlay');
 *   const element = () => { // create a DOM element, usually a div }
 *   const popup = new CustomOverlay({
 *     ...OverLayViewOptions,
 *     position: new window.google.maps.LatLng(lat,lng),
 *     content: element
 *   })
 *   popup.setMap(mapInstance);
 *
 *   return ReactDom.createPortal(<Component />, element);
 * })();
 * }
 */
class OverlayView extends window.google.maps.OverlayView {
  position = null;
  content = null;
  options = {
    contentPosition: null,
    paneLevel: 4, // default to floatPane
  };

  constructor(props) {
    super(props);
    props.position && (this.position = props.position);
    props.content && (this.content = props.content);
    props.options && (this.options = props.options);
  }

  /** Called when the popup is added to the map. */
  onAdd = () => {
    const { paneLevel } = this.options;
    const paneType = getPaneType(paneLevel);
    this.getPanes()[paneType].appendChild(this.content);
  };

  /** Called when the popup is removed from the map. */
  onRemove = () => {
    if (this.content.parentElement) {
      this.content.parentElement.removeChild(this.content);
    }
  };

  /** Called each frame when the popup needs to draw itself. */
  draw = () => {
    const divPosition = this.getProjection().fromLatLngToDivPixel(
      this.position,
    );
    // Default positioning is above the position and centered
    // divPosition is the top left position of the content. Need to calculate
    // where to shift the position of the content
    // NOTE from documentation: It is not recommended to do computationally
    // expensive work in this method.
    let xPos = divPosition.x - this.content.offsetWidth / 2;
    let yPos = divPosition.y - this.content.offsetHeight - 20; // - 20 to place content above marker

    // Determine where to position the content
    const { contentPosition, contentMapPosition } = this.options;
    // The default position of using bounds is the center of the map area
    // where xPos and yPos are both 0. Calculate how much to shift the
    // content.
    if (contentPosition === validPositions.bounds && contentMapPosition) {
      // Note: rendering an <img /> as the overlay results in
      // this.content.offsetWidth = 0 if the image is fetched at time of render.
      // Pre-load the image in the usage of this class first to check for the
      // child element and use its dimensions to calculate where to position
      // the element
      const firstChild = this.content.firstChild;
      if (firstChild && firstChild.tagName.toLowerCase() === 'img') {
        const childHeight = firstChild.offsetHeight;
        const childWidth = firstChild.offsetWidth;
        xPos = 0 - childWidth / 2;
        yPos = 0 - childHeight / 2;
        const northwestPosition = this.getProjection().fromLatLngToDivPixel(
          new window.google.maps.LatLng(
            this.options.contentMapPosition.northwest,
          ),
        );
        const southeastPosition = this.getProjection().fromLatLngToDivPixel(
          new window.google.maps.LatLng(
            this.options.contentMapPosition.southeast,
          ),
        );
        xPos = northwestPosition.x;
        yPos = northwestPosition.y;

        // Resize the image to fit within the given bounds
        firstChild.style.width =
          southeastPosition.x - northwestPosition.x + 'px';
        firstChild.style.height =
          southeastPosition.y - northwestPosition.y + 'px';
      } else {
        // If not an image fallback handling
        xPos = 0 - this.content.offsetWidth / 2;
        yPos = 0 - this.content.offsetHeight / 2;
      }
    } else if (contentPosition === validPositions.topLeft) {
      // Top left position
      xPos = 0 - divPosition.x;
      yPos = 0 - divPosition.y;
    }

    this.content.style.left = xPos + 'px';
    this.content.style.top = yPos + 'px';
  };
}

export const BOUNDS = 'bounds';
export const TOP_LEFT = 'top left';

// Valid positions for positioning the overlay
const validPositions = {
  bounds: BOUNDS,
  topLeft: TOP_LEFT,
};

export default OverlayView;
